/**
 * 数据库管理服务
 * 
 * 数据库结构：
 * 1. InfiniteNote 数据库（公共数据库）
 *    - data 表：存储 currentUser、users、appSettings 等公共数据
 * 
 * 2. IN_{uuid} 数据库（用户独立数据库）
 *    - projects 表：存储用户的项目列表
 *    - items 表：存储项目中的元素
 *    - media 表：存储媒体文件缓存
 */

// 公共数据库名称
export const MAIN_DB_NAME = 'InfiniteNote'
// 用户数据库前缀
export const USER_DB_PREFIX = 'IN_'

// 用户数据库版本
const USER_DB_VERSION = 2

// 初始化状态
export interface InitializationStatus {
  stage: 'checking' | 'creating_main_db' | 'creating_user_db' | 'loading_data' | 'complete' | 'error'
  progress: number // 0-100
  message: string
  error?: string
}

// 初始化回调
type InitProgressCallback = (status: InitializationStatus) => void

/**
 * 诊断 IndexedDB 可用性
 */
export async function diagnoseIndexedDB(): Promise<{ available: boolean; error?: string }> {
  // 检查 indexedDB 是否存在
  if (!window.indexedDB) {
    return { available: false, error: '浏览器不支持 IndexedDB' }
  }

  // 尝试创建一个测试数据库
  return new Promise((resolve) => {
    const testDBName = '__indexeddb_test__' + Date.now()
    try {
      const request = indexedDB.open(testDBName, 1)
      
      request.onerror = (event) => {
        const error = (event.target as IDBOpenDBRequest).error
        console.error('IndexedDB 诊断失败:', error)
        resolve({ 
          available: false, 
          error: error?.message || 'IndexedDB 打开失败，可能是隐私模式或存储配额问题' 
        })
      }

      request.onsuccess = () => {
        const db = request.result
        db.close()
        // 删除测试数据库
        indexedDB.deleteDatabase(testDBName)
        resolve({ available: true })
      }

      request.onblocked = () => {
        resolve({ available: false, error: 'IndexedDB 被阻塞，请关闭其他标签页后重试' })
      }

      // 超时处理
      setTimeout(() => {
        resolve({ available: false, error: 'IndexedDB 响应超时' })
      }, 5000)
    } catch (e: any) {
      resolve({ available: false, error: e.message || '未知错误' })
    }
  })
}

/**
 * 检查 InfiniteNote 数据库是否存在
 */
export async function checkMainDBExists(): Promise<boolean> {
  return new Promise((resolve) => {
    if (!window.indexedDB) {
      console.warn('浏览器不支持 IndexedDB')
      resolve(false)
      return
    }

    try {
      const request = indexedDB.open(MAIN_DB_NAME)
      let existed = true

      request.onupgradeneeded = (event) => {
        // 如果触发了 onupgradeneeded，说明数据库不存在或版本不对
        existed = false
        // 中止事务，不创建数据库
        const db = (event.target as IDBOpenDBRequest).result
        // 关闭数据库连接
        db.close()
        // 删除刚创建的空数据库
        indexedDB.deleteDatabase(MAIN_DB_NAME)
      }

      request.onsuccess = () => {
        if (!existed) {
          resolve(false)
          return
        }
        const db = request.result
        // 检查是否有 data 表
        const hasDataStore = db.objectStoreNames.contains('data')
        db.close()
        resolve(hasDataStore)
      }

      request.onerror = (event) => {
        console.error('检查主数据库失败:', (event.target as IDBOpenDBRequest).error)
        resolve(false)
      }

      request.onblocked = () => {
        console.warn('主数据库被阻塞')
        resolve(false)
      }
    } catch (e) {
      console.error('检查主数据库异常:', e)
      resolve(false)
    }
  })
}

/**
 * 检查用户数据库是否存在
 */
export async function checkUserDBExists(userUuid: string): Promise<boolean> {
  return new Promise((resolve) => {
    if (!window.indexedDB) {
      resolve(false)
      return
    }

    const dbName = `${USER_DB_PREFIX}${userUuid}`
    const request = indexedDB.open(dbName)
    let existed = true

    request.onupgradeneeded = (event) => {
      existed = false
      // 关闭数据库连接
      const db = (event.target as IDBOpenDBRequest).result
      db.close()
      // 删除刚创建的空数据库
      indexedDB.deleteDatabase(dbName)
    }

    request.onsuccess = () => {
      if (!existed) {
        resolve(false)
        return
      }
      const db = request.result
      const hasProjects = db.objectStoreNames.contains('projects')
      db.close()
      resolve(hasProjects)
    }

    request.onerror = () => {
      resolve(false)
    }

    request.onblocked = () => {
      resolve(false)
    }
  })
}

/**
 * 初始化公共数据库 InfiniteNote
 * 注意：不指定版本号，使用现有版本（兼容原生代码创建的数据库）
 */
export async function initMainDB(onProgress?: InitProgressCallback): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      const error = '浏览器不支持 IndexedDB'
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: error,
        error
      })
      reject(new Error(error))
      return
    }

    onProgress?.({
      stage: 'creating_main_db',
      progress: 10,
      message: '正在创建公共数据库...'
    })

    // 不指定版本号，让 IndexedDB 自动使用现有版本或创建新数据库
    const request = indexedDB.open(MAIN_DB_NAME)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      
      onProgress?.({
        stage: 'creating_main_db',
        progress: 30,
        message: '正在创建数据表...'
      })

      // 创建 data 表（存储公共数据）
      if (!db.objectStoreNames.contains('data')) {
        db.createObjectStore('data', { keyPath: 'id' })
      }
    }

    request.onsuccess = () => {
      onProgress?.({
        stage: 'creating_main_db',
        progress: 50,
        message: '公共数据库创建完成'
      })
      resolve(request.result)
    }

    request.onerror = () => {
      const error = '创建公共数据库失败'
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: error,
        error
      })
      reject(request.error)
    }
  })
}

/**
 * 初始化用户数据库 IN_{uuid}
 */
export async function initUserDB(userUuid: string, onProgress?: InitProgressCallback): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      const error = '浏览器不支持 IndexedDB'
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: error,
        error
      })
      reject(new Error(error))
      return
    }

    const dbName = `${USER_DB_PREFIX}${userUuid}`

    onProgress?.({
      stage: 'creating_user_db',
      progress: 60,
      message: '正在创建用户数据库...'
    })

    const request = indexedDB.open(dbName, USER_DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      onProgress?.({
        stage: 'creating_user_db',
        progress: 70,
        message: '正在创建用户数据表...'
      })

      // 创建 projects 表
      if (!db.objectStoreNames.contains('projects')) {
        const projectsStore = db.createObjectStore('projects', { keyPath: 'id' })
        projectsStore.createIndex('uuid', 'uuid', { unique: false })
        projectsStore.createIndex('opened', 'opened', { unique: false })
      }

      // 创建 items 表
      if (!db.objectStoreNames.contains('items')) {
        const itemsStore = db.createObjectStore('items', { keyPath: 'uuid' })
        itemsStore.createIndex('projectId', 'projectId', { unique: false })
      }

      // 创建 media 表（与原生代码保持一致）
      if (!db.objectStoreNames.contains('media')) {
        const mediaStore = db.createObjectStore('media', { keyPath: 'id' })
        mediaStore.createIndex('fileName', 'fileName', { unique: false })
        mediaStore.createIndex('fileType', 'fileType', { unique: false })
        mediaStore.createIndex('createdAt', 'createdAt', { unique: false })
      }
      
      // 创建 uploadedFilesCache 表（与原生代码保持一致）
      if (!db.objectStoreNames.contains('uploadedFilesCache')) {
        const uploadedStore = db.createObjectStore('uploadedFilesCache', { keyPath: 'cacheKey' })
        uploadedStore.createIndex('userUuid', 'userUuid', { unique: false })
        uploadedStore.createIndex('projectId', 'projectId', { unique: false })
      }
    }

    request.onsuccess = () => {
      onProgress?.({
        stage: 'creating_user_db',
        progress: 90,
        message: '用户数据库创建完成'
      })
      resolve(request.result)
    }

    request.onerror = () => {
      const error = '创建用户数据库失败'
      onProgress?.({
        stage: 'error',
        progress: 0,
        message: error,
        error
      })
      reject(request.error)
    }
  })
}

/**
 * 打开公共数据库
 * 注意：不指定版本号，使用现有版本（兼容原生代码创建的数据库）
 */
export async function openMainDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('浏览器不支持 IndexedDB'))
      return
    }

    // 不指定版本号，让 IndexedDB 自动使用现有版本
    const request = indexedDB.open(MAIN_DB_NAME)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result
      if (!db.objectStoreNames.contains('data')) {
        db.createObjectStore('data', { keyPath: 'id' })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * 打开用户数据库
 */
export async function openUserDB(userUuid: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      reject(new Error('浏览器不支持 IndexedDB'))
      return
    }

    const dbName = `${USER_DB_PREFIX}${userUuid}`
    const request = indexedDB.open(dbName, USER_DB_VERSION)

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result

      if (!db.objectStoreNames.contains('projects')) {
        const projectsStore = db.createObjectStore('projects', { keyPath: 'id' })
        projectsStore.createIndex('uuid', 'uuid', { unique: false })
        projectsStore.createIndex('opened', 'opened', { unique: false })
      }

      if (!db.objectStoreNames.contains('items')) {
        const itemsStore = db.createObjectStore('items', { keyPath: 'uuid' })
        itemsStore.createIndex('projectId', 'projectId', { unique: false })
      }

      // 创建 media 表（与原生代码保持一致）
      if (!db.objectStoreNames.contains('media')) {
        const mediaStore = db.createObjectStore('media', { keyPath: 'id' })
        mediaStore.createIndex('fileName', 'fileName', { unique: false })
        mediaStore.createIndex('fileType', 'fileType', { unique: false })
        mediaStore.createIndex('createdAt', 'createdAt', { unique: false })
      }
      
      // 创建 uploadedFilesCache 表（与原生代码保持一致）
      if (!db.objectStoreNames.contains('uploadedFilesCache')) {
        const uploadedStore = db.createObjectStore('uploadedFilesCache', { keyPath: 'cacheKey' })
        uploadedStore.createIndex('userUuid', 'userUuid', { unique: false })
        uploadedStore.createIndex('projectId', 'projectId', { unique: false })
      }
    }

    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

/**
 * 删除用户数据库
 */
export async function deleteUserDB(userUuid: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const dbName = `${USER_DB_PREFIX}${userUuid}`
    const request = indexedDB.deleteDatabase(dbName)
    request.onsuccess = () => resolve()
    request.onerror = () => reject(request.error)
  })
}

// ============ 公共数据库操作 ============

/**
 * 保存数据到公共数据库
 */
export async function saveToMainDB(key: string, value: any): Promise<void> {
  const db = await openMainDB()
  try {
    const transaction = db.transaction(['data'], 'readwrite')
    const store = transaction.objectStore('data')
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put({ id: key, value, updatedAt: Date.now() })
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 从公共数据库读取数据
 */
export async function getFromMainDB(key: string): Promise<any> {
  const db = await openMainDB()
  try {
    const transaction = db.transaction(['data'], 'readonly')
    const store = transaction.objectStore('data')
    
    const result = await new Promise<any>((resolve, reject) => {
      const request = store.get(key)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    
    return result?.value || null
  } finally {
    db.close()
  }
}

/**
 * 从公共数据库删除数据
 */
export async function deleteFromMainDB(key: string): Promise<void> {
  const db = await openMainDB()
  try {
    const transaction = db.transaction(['data'], 'readwrite')
    const store = transaction.objectStore('data')
    
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(key)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

// ============ 用户数据库操作 ============

/**
 * 保存项目到用户数据库
 */
export async function saveProjectToUserDB(userUuid: string, project: any): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['projects'], 'readwrite')
    const store = transaction.objectStore('projects')
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put(project)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 从用户数据库获取所有项目
 */
export async function getProjectsFromUserDB(userUuid: string): Promise<any[]> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['projects'], 'readonly')
    const store = transaction.objectStore('projects')
    
    return await new Promise<any[]>((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 从用户数据库获取单个项目
 */
export async function getProjectFromUserDB(userUuid: string, projectId: string): Promise<any | null> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['projects'], 'readonly')
    const store = transaction.objectStore('projects')
    
    return await new Promise<any | null>((resolve, reject) => {
      const request = store.get(projectId)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 从用户数据库删除项目
 */
export async function deleteProjectFromUserDB(userUuid: string, projectId: string): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['projects', 'items'], 'readwrite')
    const projectsStore = transaction.objectStore('projects')
    const itemsStore = transaction.objectStore('items')
    
    // 删除项目
    await new Promise<void>((resolve, reject) => {
      const request = projectsStore.delete(projectId)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
    
    // 删除项目相关的 items
    const itemsIndex = itemsStore.index('projectId')
    const itemsRequest = itemsIndex.getAllKeys(projectId)
    
    await new Promise<void>((resolve, reject) => {
      itemsRequest.onsuccess = async () => {
        const keys = itemsRequest.result
        for (const key of keys) {
          itemsStore.delete(key)
        }
        resolve()
      }
      itemsRequest.onerror = () => reject(itemsRequest.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 保存 items 到用户数据库
 */
export async function saveItemsToUserDB(userUuid: string, items: any[]): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['items'], 'readwrite')
    const store = transaction.objectStore('items')
    
    for (const item of items) {
      await new Promise<void>((resolve, reject) => {
        const request = store.put(item)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    }
  } finally {
    db.close()
  }
}

/**
 * 从用户数据库获取项目的 items
 */
export async function getItemsFromUserDB(userUuid: string, projectId: string): Promise<any[]> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['items'], 'readonly')
    const store = transaction.objectStore('items')
    const index = store.index('projectId')
    
    return await new Promise<any[]>((resolve, reject) => {
      const request = index.getAll(projectId)
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 从用户数据库获取所有 items
 */
export async function getAllItemsFromUserDB(userUuid: string): Promise<any[]> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['items'], 'readonly')
    const store = transaction.objectStore('items')
    
    return await new Promise<any[]>((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 删除用户数据库中的 item
 */
export async function deleteItemFromUserDB(userUuid: string, itemUuid: string): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['items'], 'readwrite')
    const store = transaction.objectStore('items')
    
    await new Promise<void>((resolve, reject) => {
      const request = store.delete(itemUuid)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

// ============ 媒体缓存操作 ============

/**
 * 保存媒体到用户数据库
 */
export async function saveMediaToUserDB(userUuid: string, media: any): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['media'], 'readwrite')
    const store = transaction.objectStore('media')
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put(media)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 从用户数据库获取媒体
 */
export async function getMediaFromUserDB(userUuid: string, mediaId: string): Promise<any | null> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['media'], 'readonly')
    const store = transaction.objectStore('media')
    
    return await new Promise<any | null>((resolve, reject) => {
      const request = store.get(mediaId)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 从 localStorage 迁移 appSettings 到 InfiniteNote 数据库
 * 只在 InfiniteNote 数据库中没有 appSettings 时执行迁移
 */
export async function migrateAppSettingsFromLocalStorage(): Promise<void> {
  try {
    // 检查 InfiniteNote 数据库中是否已有 appSettings
    const existingSettings = await getFromMainDB('appSettings')
    if (existingSettings) {
      // 已有设置，不需要迁移
      return
    }

    // 尝试从 localStorage 读取
    const savedJson = localStorage.getItem('appSettings')
    if (savedJson) {
      try {
        const settings = JSON.parse(savedJson)
        // 保存到 InfiniteNote 数据库
        await saveToMainDB('appSettings', settings)
        console.log('[migrateAppSettings] 已从 localStorage 迁移 appSettings 到 InfiniteNote 数据库')
        
        // 迁移成功后删除 localStorage 中的数据
        localStorage.removeItem('appSettings')
        console.log('[migrateAppSettings] 已删除 localStorage 中的 appSettings')
      } catch (e) {
        console.warn('[migrateAppSettings] 解析 localStorage 中的 appSettings 失败:', e)
      }
    }
  } catch (error) {
    console.warn('[migrateAppSettings] 迁移 appSettings 失败:', error)
  }
}

/**
 * 清理所有 localStorage 中的旧数据
 */
export function clearLegacyLocalStorage(): void {
  const keysToRemove = [
    'appData',
    'appSettings', // 现在使用 IndexedDB，不再需要 localStorage
    'infiniteNote_users',
    'infiniteNote_currentUser'
  ]
  
  // 清理带前缀的用户数据
  const allKeys = Object.keys(localStorage)
  for (const key of allKeys) {
    if (key.startsWith('infiniteNote_userData_')) {
      keysToRemove.push(key)
    }
  }
  
  for (const key of keysToRemove) {
    try {
      localStorage.removeItem(key)
    } catch (e) {
      console.warn(`清理 localStorage key ${key} 失败:`, e)
    }
  }
}

/**
 * 获取所有用户数据库列表
 */
export async function listUserDatabases(): Promise<string[]> {
  if (!window.indexedDB.databases) {
    // 某些浏览器不支持 databases() 方法
    console.warn('浏览器不支持 indexedDB.databases()')
    return []
  }
  
  try {
    const databases = await window.indexedDB.databases()
    return databases
      .filter(db => db.name?.startsWith(USER_DB_PREFIX))
      .map(db => db.name!.replace(USER_DB_PREFIX, ''))
  } catch (e) {
    console.error('获取数据库列表失败:', e)
    return []
  }
}

/**
 * 更新项目的 opened 状态
 */
export async function updateProjectOpened(userUuid: string, projectId: string, opened: boolean): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['projects'], 'readwrite')
    const store = transaction.objectStore('projects')
    
    const project = await new Promise<any>((resolve, reject) => {
      const request = store.get(projectId)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    
    if (project) {
      project.opened = opened
      await new Promise<void>((resolve, reject) => {
        const request = store.put(project)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    }
  } finally {
    db.close()
  }
}

/**
 * 获取已打开的项目列表
 */
export async function getOpenedProjects(userUuid: string): Promise<any[]> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['projects'], 'readonly')
    const store = transaction.objectStore('projects')
    const index = store.index('opened')
    
    return await new Promise<any[]>((resolve, reject) => {
      // 使用 IDBKeyRange.only(true) 来查询 opened === true 的项目
      const request = index.getAll(IDBKeyRange.only(true))
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

// ============== 已上传文件缓存操作 ==============

/**
 * 保存已上传文件缓存记录
 */
export async function saveUploadedFileCacheToUserDB(userUuid: string, cacheData: {
  cacheKey: string
  userUuid: string
  projectId: string
  fileId: string
  storageProvider: string
  uploadedAt: string
}): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['uploadedFilesCache'], 'readwrite')
    const store = transaction.objectStore('uploadedFilesCache')
    
    await new Promise<void>((resolve, reject) => {
      const request = store.put(cacheData)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 获取已上传文件缓存记录
 */
export async function getUploadedFileCacheFromUserDB(userUuid: string, cacheKey: string): Promise<any | null> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['uploadedFilesCache'], 'readonly')
    const store = transaction.objectStore('uploadedFilesCache')
    
    return await new Promise<any | null>((resolve, reject) => {
      const request = store.get(cacheKey)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 删除项目的已上传文件缓存
 */
export async function clearUploadedFileCacheForProject(userUuid: string, projectId: string): Promise<void> {
  const db = await openUserDB(userUuid)
  try {
    const transaction = db.transaction(['uploadedFilesCache'], 'readwrite')
    const store = transaction.objectStore('uploadedFilesCache')
    const index = store.index('projectId')
    
    // 获取该项目的所有缓存记录
    const records = await new Promise<any[]>((resolve, reject) => {
      const request = index.getAll(IDBKeyRange.only(projectId))
      request.onsuccess = () => resolve(request.result || [])
      request.onerror = () => reject(request.error)
    })
    
    // 删除所有记录
    for (const record of records) {
      await new Promise<void>((resolve, reject) => {
        const request = store.delete(record.cacheKey)
        request.onsuccess = () => resolve()
        request.onerror = () => reject(request.error)
      })
    }
  } finally {
    db.close()
  }
}
